import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../app/hooks";
import { MessagesChannel } from "../../features/messages/models/MessagesChannel";
import { fetchAttachmentUrl } from "../../features/messages/slice-extensions/attachments-slice-extension";

/**
 * Fetches a fresh five-minute URL for an attachment when it is opened, never earlier. `inNewTab`
 * opens the tab synchronously (so no popup blocker stops it) and points it at the URL once known.
 */
export const useOpenAttachment = (channel: MessagesChannel) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const fetchUrl = async (attachmentId: string): Promise<string | null> => {
    try {
      return (await dispatch(fetchAttachmentUrl({ channel, attachmentId })).unwrap()).url;
    } catch {
      toast.error(t("messages.attach.openFailed"));
      return null;
    }
  };

  const inNewTab = async (attachmentId: string): Promise<void> => {
    const tab = window.open("", "_blank");
    const url = await fetchUrl(attachmentId);
    if (!url) {
      tab?.close();
      return;
    }
    if (tab) {
      tab.opener = null;
      tab.location.href = url;
    } else {
      window.location.assign(url);
    }
  };

  /** A file's URL answers with an attachment disposition, so the browser saves it in place. */
  const download = async (attachmentId: string): Promise<void> => {
    const url = await fetchUrl(attachmentId);
    if (url) window.location.assign(url);
  };

  return { fetchUrl, inNewTab, download };
};
